<template>
  <div class="enhanced-model-page">
    <v-container
      v-if="loading"
      class="d-flex justify-center align-center"
      style="min-height: 80vh"
    >
      <v-progress-circular
        :size="100"
        :width="8"
        indeterminate
        color="primary"
      ></v-progress-circular>
    </v-container>

    <v-container v-else-if="error" class="text-center">
      <v-alert type="error" class="mt-10">
        <h3>Error Loading Model Data</h3>
        <p>{{ error }}</p>
      </v-alert>
    </v-container>

    <template v-else-if="modelData">
      <section class="hero-section">
        <div
          class="hero-background"
          :style="{
            backgroundImage: `url(${cdnUrl}/lightning-model-page/cx/city-backdrop.jpg)`,
          }"
        >
          <v-container class="hero-container">
            <v-row class="align-center justify-center">
              <v-col cols="12" class="text-center py-8">
                <div class="hero-badge mb-4">{{ modelData.year }} Model</div>
                <h1 class="hero-title mb-4">
                  {{ modelData.make }} {{ modelData.model }}
                </h1>
                <div class="hero-price-container">
                  <span class="hero-price-label">Starting at</span>
                  <span class="hero-price">{{
                    formatCurrency(lowestPrice)
                  }}</span>
                </div>
              </v-col>
            </v-row>
            <v-row class="justify-center">
              <v-col cols="12" lg="10" xl="8" class="d-flex justify-center">
                <div class="hero-image-wrapper">
                  <v-img
                    :src="currentVehicleImage"
                    :alt="`${modelData.year} ${modelData.make} ${modelData.model}`"
                    class="hero-vehicle-image"
                  ></v-img>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </section>

      <section class="trims-section py-12 bg-grey-lighten-4">
        <v-container>
          <div class="section-header mb-10">
            <h2 class="section-title">Available Trims</h2>
            <p class="section-subtitle">
              Choose from {{ modelData.trims.length }} trim options
              <v-chip
                v-if="comparisonTrims.length > 0"
                color="primary"
                size="small"
                class="ml-2"
              >
                {{ comparisonTrims.length }} selected for comparison
              </v-chip>
            </p>
          </div>
          <v-row>
            <v-col
              v-for="(trim, index) in modelData.trims"
              :key="trim.id || index"
              cols="12"
              sm="6"
              md="6"
              lg="4"
              xl="3"
            >
              <v-card
                class="trim-card"
                :class="{ 'selected-trim': selectedTrim?.id === trim.id }"
                elevation="3"
                hover
              >
                <div class="trim-image-container">
                  <v-img
                    :src="trim.img || noVehicleImage"
                    height="220"
                    cover
                    class="trim-image"
                  >
                    <template v-slot:placeholder>
                      <div
                        class="d-flex align-center justify-center fill-height"
                      >
                        <v-progress-circular
                          indeterminate
                          color="primary"
                        ></v-progress-circular>
                      </div>
                    </template>
                  </v-img>
                  <v-chip
                    v-if="selectedTrim?.id === trim.id"
                    color="primary"
                    size="small"
                    class="selected-badge"
                  >
                    Selected
                  </v-chip>
                </div>
                <v-card-title class="trim-title">
                  {{ trim.trim || trim.styleName }}
                </v-card-title>
                <v-card-subtitle class="trim-price">
                  {{ formatCurrency(trim.baseMsrp || trim.price) }}
                </v-card-subtitle>
                <v-card-text class="py-3">
                  <div class="trim-features">
                    <v-chip
                      v-for="(feature, idx) in getTopFeatures(trim)"
                      :key="idx"
                      size="small"
                      variant="tonal"
                      color="primary"
                      class="mr-1 mb-1"
                    >
                      {{ feature }}
                    </v-chip>
                  </div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="px-4 py-3">
                  <v-btn
                    :color="isInComparison(trim.id) ? 'success' : 'primary'"
                    :variant="isInComparison(trim.id) ? 'tonal' : 'text'"
                    size="small"
                    @click.stop.prevent="addToComparison(trim)"
                    :prepend-icon="
                      isInComparison(trim.id)
                        ? 'mdi-check-circle'
                        : 'mdi-compare'
                    "
                  >
                    {{ isInComparison(trim.id) ? 'Added' : 'Compare' }}
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    variant="flat"
                    size="small"
                    @click.stop="selectTrim(trim)"
                  >
                    Details
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <section
        v-if="comparisonTrims.length > 1"
        class="comparison-section py-12"
      >
        <v-container>
          <div class="section-header mb-8">
            <h2 class="section-title">Trim Comparison</h2>
            <p class="section-subtitle">
              Comparing {{ comparisonTrims.length }} trims side by side
            </p>
          </div>
          <div class="d-flex justify-end mb-4">
            <v-btn
              color="error"
              variant="outlined"
              @click="clearComparison"
              prepend-icon="mdi-close-circle"
            >
              Clear Comparison
            </v-btn>
          </div>
          <TrimComparisonTable
            :trims="comparisonTrims"
            :modelData="modelData"
            @remove-trim="removeTrimFromComparison"
          />
        </v-container>
      </section>

      <section
        v-if="selectedTrim"
        class="specifications-section py-12 bg-grey-lighten-4"
      >
        <v-container>
          <div class="section-header mb-8">
            <h2 class="section-title">
              {{ selectedTrim.trim || selectedTrim.styleName }}
            </h2>
            <p class="section-subtitle">Detailed specifications and features</p>
          </div>
          <SpecificationsTabs :trim="selectedTrim" />
        </v-container>
      </section>

      <section class="cross-model-section py-12">
        <v-container class="cross-model-section__container">
          <div class="section-header mb-8">
            <h2 class="section-title">Compare with Other Models</h2>
            <p class="section-subtitle">
              See how this vehicle stacks up against competitors
            </p>
          </div>
          <CrossModelComparison
            :currentModel="modelData"
            :currentTrim="selectedTrim"
          />
        </v-container>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import TrimComparisonTable from '@/components/TrimComparisonTable.vue'
import SpecificationsTabs from '@/components/SpecificationsTabs.vue'
import CrossModelComparison from '@/components/CrossModelComparison.vue'
import { fetchDataHubModel } from '@/apis'
import { formatCurrency, getCDNUrl } from '@/utils'
import { parseSpecItem, parseSpecValue } from '@/utils/modelDetails'

const route = useRoute()
const cdnUrl = getCDNUrl()
const noVehicleImage = `${cdnUrl}/lightning-model-page/cx/no-vehicle-image.png`

const loading = ref(true)
const error = ref<string | null>(null)
const modelData = ref<any>(null)
const selectedTrim = ref<any>(null)
const comparisonTrims = ref<any[]>([])

const lowestPrice = computed(() => {
  if (!modelData.value?.trims?.length) return 0
  const prices = modelData.value.trims
    .map((t: any) => Number(t.baseMsrp || t.price || 0))
    .filter((p: number) => p > 0)
  return prices.length ? Math.min(...prices) : 0
})

const currentVehicleImage = computed(() => {
  return (
    selectedTrim.value?.img ||
    modelData.value?.trims?.[0]?.img ||
    noVehicleImage
  )
})

const fetchModelData = async () => {
  try {
    loading.value = true
    error.value = null

    const { year, make, model } = routeParams.value

    if (!year || !make || !model) {
      throw new Error('Year, make, and model are required')
    }

    const response = await fetchDataHubModel(
      Number(year),
      String(make),
      String(model),
    )

    if (response) {
      modelData.value = response
      if (response.trims?.length > 0) {
        selectedTrim.value = response.trims[0]
      }
    } else {
      throw new Error('No data available for this model')
    }
  } catch (err) {
    console.error('Error fetching model data:', err)
    error.value =
      err instanceof Error ? err.message : 'Failed to load model data'
  } finally {
    loading.value = false
  }
}

const selectTrim = (trim: any) => {
  selectedTrim.value = trim
}

const getTopFeatures = (trim: any) => {
  const features = []

  const engine = trim.technicalSpecification?.Engine?.items?.[0]
  if (engine) {
    const parsed = parseSpecItem(engine, false)
    const label = typeof parsed === 'object' ? parsed.label : engine
    features.push(label)
  }

  const drivetrain = trim.technicalSpecification?.Transmission?.items?.find(
    (item: string) => item.includes('Drivetrain'),
  )
  if (drivetrain) {
    const value = parseSpecValue(drivetrain)
    if (value && value !== 'N/A') features.push(value)
  }

  const seating = trim.technicalSpecification?.[
    'Interior Dimensions'
  ]?.items?.find((item: string) => item.includes('Passenger Capacity'))
  if (seating) {
    const value = parseSpecValue(seating)
    if (value && value !== 'N/A') features.push(`${value} Seating`)
  }

  return features.slice(0, 3)
}

const addToComparison = (trim: any) => {
  const existingIndex = comparisonTrims.value.findIndex(t => t.id === trim.id)

  if (existingIndex !== -1) {
    comparisonTrims.value.splice(existingIndex, 1)
  } else {
    if (comparisonTrims.value.length < 4) {
      comparisonTrims.value.push(trim)
    } else {
      alert(
        'Maximum 4 trims can be compared at once. Please remove a trim first.',
      )
    }
  }
}

const isInComparison = (trimId: string): boolean => {
  return comparisonTrims.value.some(t => t.id === trimId)
}

const removeTrimFromComparison = (trimId: string) => {
  comparisonTrims.value = comparisonTrims.value.filter(t => t.id !== trimId)
}

const clearComparison = () => {
  comparisonTrims.value = []
}

const getModelParams = (): { year: string; make: string; model: string } => {
  const routeParams = route?.params ?? {}
  let year: string | undefined = routeParams.year as string | undefined
  let make: string | undefined = routeParams.make as string | undefined
  let model: string | undefined = routeParams.model as string | undefined

  if ((!year || !make || !model) && window.location.hash.length > 1) {
    const hashParams = window.location.hash.substring(1).split('/')
    if (hashParams.length >= 3) {
      year = year || decodeURIComponent(hashParams[0])
      make = make || decodeURIComponent(hashParams[1])
      model = model || decodeURIComponent(hashParams[2])
    }
  }

  if (!year || !make || !model) {
    const queryParams = new URLSearchParams(window.location.search)
    year = year || queryParams.get('year') || undefined
    make = make || queryParams.get('make') || undefined
    model = model || queryParams.get('model') || undefined
  }

  return {
    year: year ? decodeURIComponent(String(year)) : '',
    make: make ? decodeURIComponent(String(make)) : '',
    model: model ? decodeURIComponent(String(model)) : '',
  }
}

const routeParams = computed(() => {
  return getModelParams()
})

watch(
  () => routeParams.value,
  () => {
    fetchModelData()
  },
  { deep: true },
)

let hashChangeHandler: (() => void) | null = null

onMounted(() => {
  fetchModelData()
  
  hashChangeHandler = () => {
    fetchModelData()
  }
  window.addEventListener('hashchange', hashChangeHandler)
})

onUnmounted(() => {
  if (hashChangeHandler) {
    window.removeEventListener('hashchange', hashChangeHandler)
  }
})
</script>

<style scoped>
.enhanced-model-page {
  min-height: 100vh;
  background: linear-gradient(to bottom, #fafafa, #ffffff);
  scroll-behavior: smooth;
}

:deep(*) {
  scroll-behavior: smooth;
}

.hero-section {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.hero-background {
  background-size: cover;
  background-position: center;
  background-blend-mode: soft-light;
  padding: 60px 0 80px;
  position: relative;
  min-height: 600px;
}

.hero-background::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(102, 126, 234, 0.85) 0%,
    rgba(118, 75, 162, 0.75) 40%,
    rgba(0, 0, 0, 0.3) 100%
  );
  z-index: 1;
}

.hero-container {
  position: relative;
  z-index: 2;
}

.hero-badge {
  display: inline-block;
  padding: 8px 20px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  color: white;
  text-shadow: 2px 4px 8px rgba(0, 0, 0, 0.3);
  letter-spacing: -1px;
  line-height: 1.2;
}

.hero-price-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.hero-price-label {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.hero-price {
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  text-shadow: 1px 2px 4px rgba(0, 0, 0, 0.2);
}

.hero-image-wrapper {
  width: 100%;
  max-width: 900px;
  margin-top: 40px;
}

.hero-vehicle-image {
  filter: drop-shadow(0 30px 60px rgba(0, 0, 0, 0.4));
  transition: transform 0.3s ease;
}

.hero-vehicle-image:hover {
  transform: scale(1.02);
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
  position: relative;
}

.section-header::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(
    90deg,
    transparent,
    rgb(var(--v-theme-primary)),
    transparent
  );
  border-radius: 2px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 12px;
  letter-spacing: -1px;
  background: linear-gradient(135deg, #1a1a1a, #4a5568);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-subtitle {
  font-size: 1.125rem;
  color: #718096;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.trim-card {
  height: 100%;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border-radius: 16px;
  overflow: hidden;
  background: white;
  border: 2px solid transparent;
}

.trim-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  border-color: rgba(var(--v-theme-primary), 0.2);
}

.trim-image-container {
  position: relative;
  overflow: hidden;
  background: linear-gradient(to bottom, #f8f9fa, #ffffff);
}

.trim-image {
  transition: all 0.4s ease;
}

.trim-card:hover .trim-image {
  transform: scale(1.08);
  filter: brightness(1.05);
}

.selected-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  animation: fadeInScale 0.3s ease;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.selected-trim {
  border: 3px solid rgb(var(--v-theme-primary));
  box-shadow: 0 16px 50px rgba(25, 118, 210, 0.35);
  transform: translateY(-6px);
  background: linear-gradient(to bottom, #ffffff, #f8f9ff);
}

.selected-trim:hover {
  transform: translateY(-10px) scale(1.02);
}

.trim-title {
  font-size: 1.125rem;
  font-weight: 700;
  padding-bottom: 4px;
  color: #2c3e50;
  line-height: 1.4;
}

.trim-price {
  font-size: 1.5rem;
  font-weight: 800;
  color: rgb(var(--v-theme-primary));
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)), #667eea);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.trim-features {
  min-height: 68px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: flex-start;
}

@media (max-width: 1280px) {
  .hero-title {
    font-size: 2.75rem;
  }

  .hero-price {
    font-size: 2rem;
  }
}

@media (max-width: 960px) {
  .hero-background {
    padding: 40px 0 60px;
    min-height: 500px;
  }

  .hero-title {
    font-size: 2rem;
  }

  .hero-price {
    font-size: 1.75rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .section-subtitle {
    font-size: 1rem;
  }
}

.cross-model-section,
.cross-model-section__container {
  overflow: visible;
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 1.75rem;
  }

  .hero-price {
    font-size: 1.5rem;
  }

  .hero-badge {
    font-size: 0.75rem;
    padding: 6px 16px;
  }

  .section-title {
    font-size: 1.75rem;
  }
}
</style>
